import { z } from 'zod';

import { mongoIdSchema } from './shared';

export enum ContentKind {
  Article = 'article',
}

export enum ContentStatus {
  Ok = 'ok',

  Analysing = 'analysing', // ai
  AnalysisError = 'analysisError', // ai failed

  Refreshing = 'refreshing', // ai
  RefreshError = 'refreshError', // ai failed

  Locked = 'locked', // can be used by admin to block the user from accessing
  Removed = 'removed', // we might want to keep in store the removed
}

export enum ContentUserStatus {
  Draft = 'draft',
  Review = 'review',
  Approved = 'approved',
  Published = 'published',
}

export const contentCreateSchema = z.object({
  topic: z.string().trim().nullish().default(''),
  primaryKeyword: z.string().trim(),
  primaryPrompt: z.string().trim().nullish().default(''),
  secondaryKeywords: z.array(z.string().trim()),

  title: z.string().trim(),
  // body is a tiptap json object
  body: z.string().trim(),
  // TODO: should have a better rule for language
  language: z.string().trim(),
  country: z.string().trim(),
  kind: z.enum([ContentKind.Article]).nullish(), // LEGACY: contents are to be articles now
  description: z.string().trim(),
  metaTitle: z.string().trim(),
  metaDescription: z.string().trim(),
  urlSlug: z.string().trim().nullish(),
  brandId: mongoIdSchema,
  organizationId: mongoIdSchema,
  isOneClickGeneration: z.boolean().nullish(),

  userStatus: z.nativeEnum(ContentUserStatus).nullish(),
  publicationDate: z.number().nullish(),
  publicationUrl: z.string().trim().nullish(),
});
export type ContentCreate = z.infer<typeof contentCreateSchema>;

type ContentOutline = {
  tagKind: string;
  value: string;
  aiInstructions: string[];
  children: ContentOutline[];
};

const contentOutlineSchema = z.object({
  tagKind: z.string().trim().min(1),
  value: z.string().trim(),
  aiInstructions: z.array(z.string().trim()),
  children: z.lazy(() => contentOutlineSchema.array()) as z.ZodType<ContentOutline[]>,
}) as z.ZodType<ContentOutline>;

export const contentSchema = z
  .object({
    _id: mongoIdSchema,
    createdAt: z.number(),
    updatedAt: z.number(),

    userRating: z
      .object({
        general: z.number(),
        infoQuality: z.number().nullish(),
        structureQuality: z.number().nullish(),
        toneVoice: z.number().nullish(),
        comments: z.string().nullish(),
      })
      .nullish(),

    createdBy: mongoIdSchema,

    seoScore: z.number(),
    wordCount: z.number(),

    status: z.nativeEnum(ContentStatus),
  })
  .merge(contentCreateSchema)
  // used to remove extras that we might need internally but not externally
  // for example: isArchived
  .strip();

export type Content = z.infer<typeof contentSchema>;
